/**
 * PerfusedVolumeBuilder — UI for creating/editing a perfused volume.
 *
 * Displays segment/tumor checkboxes, computed volume & hepatic reserve,
 * smart name (editable), and Save/Cancel buttons.
 */

import { useState, useMemo, useEffect } from 'react';

import type { SegmentEntry } from './segment-list';
import { SegmentList } from './segment-list';
import { getSmartName, getHepaticReserveStatus, computeHepaticReserve } from '../domain/perfused-volume';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

export interface PerfusedVolumeBuilderProps {
    segments: readonly SegmentEntry[];
    tumors: readonly SegmentEntry[];
    totalLiverVolumeMl: number;
    /** Compute the union volume for a given selection of segments and tumors */
    computeUnionVolumeMl: (segmentIndices: readonly number[], tumorIndices: readonly number[]) => number;
    /** Initial selections when editing an existing perfused volume */
    initialSegments?: readonly number[];
    initialTumors?: readonly number[];
    initialName?: string;
    onSave: (segmentIndices: number[], tumorIndices: number[], name?: string) => void;
    onCancel: () => void;
    onHoverSegment?: (labelValue: number | null) => void;
    onHoverTumor?: (labelValue: number | null) => void;
    /** Called when checkbox selection changes (segments and/or tumors toggled) */
    onSelectionChanged?: (selectedSegments: ReadonlySet<number>, selectedTumors: ReadonlySet<number>) => void;
}

// ============================================================================
// Component
// ============================================================================

export function PerfusedVolumeBuilder({
    segments,
    tumors,
    totalLiverVolumeMl,
    computeUnionVolumeMl,
    initialSegments = [],
    initialTumors = [],
    initialName,
    onSave,
    onCancel,
    onHoverSegment,
    onHoverTumor,
    onSelectionChanged,
}: PerfusedVolumeBuilderProps) {
    const [selectedSegs, setSelectedSegs] = useState<Set<number>>(new Set(initialSegments));
    const [selectedTums, setSelectedTums] = useState<Set<number>>(new Set(initialTumors));
    const [customName, setCustomName] = useState(initialName ?? '');

    const toggleSegment = (lv: number) => {
        setSelectedSegs((prev) => {
            const next = new Set(prev);
            if (next.has(lv)) next.delete(lv);
            else next.add(lv);
            return next;
        });
    };

    const toggleTumor = (lv: number) => {
        setSelectedTums((prev) => {
            const next = new Set(prev);
            if (next.has(lv)) next.delete(lv);
            else next.add(lv);
            return next;
        });
    };

    // Notify parent of selection changes (including initial mount)
    useEffect(() => {
        onSelectionChanged?.(selectedSegs, selectedTums);
    }, [selectedSegs, selectedTums]);

    const segArray = useMemo(() => [...selectedSegs].sort((a, b) => a - b), [selectedSegs]);
    const tumArray = useMemo(() => [...selectedTums].sort((a, b) => a - b), [selectedTums]);

    const smartName = useMemo(() => getSmartName(segArray), [segArray]);
    const displayName = customName || smartName;

    const selectedVolumeMl = useMemo(
        () => computeUnionVolumeMl(segArray, tumArray),
        [computeUnionVolumeMl, segArray, tumArray]
    );

    const hepaticReservePercent = useMemo(
        () => computeHepaticReserve(totalLiverVolumeMl, selectedVolumeMl),
        [totalLiverVolumeMl, selectedVolumeMl]
    );

    const reserveStatus = getHepaticReserveStatus(hepaticReservePercent);

    const handleSave = () => {
        if (segArray.length === 0) return;
        onSave(segArray, tumArray, customName || undefined);
    };

    return (
        <div className="flex flex-col gap-2">
            {/* Name input */}
            <div className="flex w-full items-center gap-1 px-2 py-2 text-sm font-semibold font-mono uppercase tracking-wide text-muted-foreground">
                [Perfused Volume Name]
            </div>
            <div className="mb-2 px-2">
                <Input
                    placeholder={smartName || 'Perfused volume name'}
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    className="h-8 text-sm font-mono"
                />
            </div>

            {/* Selection list */}
            <SegmentList
                segments={segments}
                tumors={tumors}
                totalLiverVolumeMl={totalLiverVolumeMl}
                selectedSegments={selectedSegs}
                selectedTumors={selectedTums}
                onToggleSegment={toggleSegment}
                onToggleTumor={toggleTumor}
                onHoverSegment={onHoverSegment}
                onHoverTumor={onHoverTumor}
            />

            {/* Summary */}
            <div className="mx-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs">
                <div className="flex justify-between">
                    <span className="text-muted-foreground">Volume</span>
                    <span className="tabular-nums font-mono font-medium">
                        {selectedVolumeMl.toFixed(1)} mL
                    </span>
                </div>
                <div className="flex justify-between mt-1">
                    <span className="text-muted-foreground">Hepatic Reserve</span>
                    <span
                        className={cn(
                            'tabular-nums font-mono font-medium',
                            reserveStatus === 'critical' && 'text-red-500',
                            reserveStatus === 'warning' && 'text-orange-500'
                        )}
                    >
                        {hepaticReservePercent.toFixed(1)}%
                    </span>
                </div>
                {displayName && (
                    <div className="flex justify-between mt-1">
                        <span className="text-muted-foreground">Name</span>
                        <span className="font-mono font-medium truncate ml-4">{displayName}</span>
                    </div>
                )}
            </div>

            {/* Actions */}
            <div className="flex gap-2 px-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={onCancel}>
                    Cancel
                </Button>
                <Button size="sm" className="flex-1" disabled={segArray.length === 0} onClick={handleSave}>
                    Save
                </Button>
            </div>
        </div>
    );
}
